import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../hooks/useNavigation';
import type { CampaignTemplate, TemplateListParams } from '../../types/campaignTemplate';
import { ExecuteTemplateModal } from './ExecuteTemplateModal';
import { CreateTemplateModal } from './CreateTemplateModal';
import ConfirmDialog, { useConfirmDialog } from '../ui/ConfirmDialog';

interface CampaignTemplatesTabProps {
  wabaAccountId: string;
  qualityRating?: string;
}

export default function CampaignTemplatesTab({ wabaAccountId, qualityRating }: CampaignTemplatesTabProps) {
  const { accessToken } = useAuth();
  const { navigate, navigateReplace, location } = useNavigation();

  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTemplates, setTotalTemplates] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modals
  const [executeModalOpen, setExecuteModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CampaignTemplate | null>(null);
  const { confirm: showConfirm, dialogProps } = useConfirmDialog();

  const fetchTemplates = useCallback(async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);

    try {
      const params: TemplateListParams = {
        page: currentPage,
        limit: 9,
        isActive: !showInactive ? true : undefined,
        search: searchTerm || undefined,
        wabaAccountId, // Obrigatório - filtra templates por conta WABA
      };

      const response = await apiClient.getCampaignTemplates(params, accessToken);

      if (response.success && response.data) {
        setTemplates(response.data.items);
        setTotalPages(response.data.pagination.pages);
        setTotalTemplates(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  }, [accessToken, currentPage, searchTerm, showInactive, wabaAccountId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Detectar se voltou da página de histórico com intenção de executar template
  useEffect(() => {
    const state = location.state as { executeTemplateId?: string } | null;
    if (state?.executeTemplateId && templates.length > 0) {
      const template = templates.find(t => t.id === state.executeTemplateId);
      if (template && template.isActive) {
        handleExecuteTemplate(template);
        // Limpar o state para não reabrir o modal
        navigateReplace(location.pathname, {});
      }
    }
  }, [location.state, templates, navigateReplace, location.pathname]);

  const handleExecuteTemplate = (template: CampaignTemplate) => {
    setSelectedTemplate(template);
    setExecuteModalOpen(true);
  };

  const handleExecuteSuccess = (campaignId: string) => {
    navigate(`/waba/${wabaAccountId}/campaigns?highlight=${campaignId}`);
  };

  const handleToggleActive = async (template: CampaignTemplate) => {
    if (!accessToken) return;

    try {
      await apiClient.updateCampaignTemplate(
        template.id,
        { isActive: !template.isActive },
        accessToken
      );
      fetchTemplates();
    } catch (err: any) {
      setError(`Erro: ${err.message}`);
    }
  };

  const handleDeleteTemplate = (template: CampaignTemplate) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Desativar modelo',
      message: `Deseja desativar o modelo "${template.name}"? O historico de execucoes sera preservado.`,
      variant: 'danger',
      confirmText: 'Desativar',
      onConfirm: async () => {
        try {
          await apiClient.deleteCampaignTemplate(template.id, accessToken);
          fetchTemplates();
        } catch (err: any) {
          setError(`Erro: ${err.message}`);
        }
      },
    });
  };

  if (loading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-1 mx-auto mb-4"></div>
          <p className="text-text-2">Carregando templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-text-1">Campanhas de Disparo</h2>
          <p className="text-text-3 text-sm mt-1">
            Campanhas reutilizáveis para disparos recorrentes
          </p>
        </div>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="px-6 py-3 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90 font-medium flex items-center gap-2"
        >
          <span>+</span>
          Nova Campanha
        </button>
      </div>

      {/* Filters */}
      <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-sm p-4 mb-6 flex items-center gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-4 py-2 bg-bg-1 border border-bg-3 rounded-lg text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:border-transparent"
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => {
              setShowInactive(e.target.checked);
              setCurrentPage(1);
            }}
            className="w-4 h-4 text-accent-1 rounded focus:ring-accent-1"
          />
          <span className="text-sm text-text-2">Mostrar inativos</span>
        </label>

        <div className="text-sm text-text-3">
          {totalTemplates} modelo{totalTemplates !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-danger/10 border border-danger rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-danger">⚠️</span>
            <p className="text-danger">{error}</p>
          </div>
        </div>
      )}

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-12 text-center">
          <div className="text-text-3 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-text-1 mb-2">Nenhum modelo encontrado</h3>
          <p className="text-text-2 mb-6">
            {searchTerm
              ? 'Tente ajustar os filtros de busca'
              : 'Comece criando sua primeira campanha de disparo'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setCreateModalOpen(true)}
              className="px-6 py-3 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90 font-medium"
            >
              Criar Primeira Campanha
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className={`bg-bg-2 border-2 rounded-lg shadow-sm hover:shadow-md transition-shadow ${
                template.isActive ? 'border-bg-3' : 'border-bg-3 opacity-60'
              }`}
            >
              {/* Card Header */}
              <div className="p-6 border-b border-bg-3">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-text-1 flex-1 pr-2">
                    {template.name}
                  </h3>
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${
                      template.isActive
                        ? 'bg-success/10 text-success'
                        : 'bg-bg-3 text-text-3'
                    }`}
                  >
                    {template.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </div>

                {template.description && (
                  <p className="text-sm text-text-2 mb-3">{template.description}</p>
                )}

                <div className="text-sm text-text-3 space-y-1">
                  <p>
                    <span className="font-medium">Template WhatsApp:</span> {template.templateName}
                  </p>
                  <p>
                    <span className="font-medium">Idioma:</span> {template.templateLanguage}
                  </p>
                  <p>
                    <span className="font-medium">Tipo:</span>{' '}
                    {template.type === 'broadcast' ? 'Broadcast' : 'Transacional'}
                  </p>
                </div>
              </div>

              {/* Card Stats */}
              <div className="p-4 bg-bg-1 border-b border-bg-3">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-accent-1">
                      {template.totalExecutions || 0}
                    </p>
                    <p className="text-xs text-text-3">Disparos</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-success">
                      {template.stats?.avgDeliveryRate?.toFixed(1) || '0'}%
                    </p>
                    <p className="text-xs text-text-3">Taxa Entrega</p>
                  </div>
                </div>

                {template.lastExecutedAt && (
                  <p className="text-xs text-text-3 text-center mt-3">
                    Último disparo:{' '}
                    {new Date(template.lastExecutedAt).toLocaleDateString('pt-BR')}
                  </p>
                )}
              </div>

              {/* Card Actions */}
              <div className="p-4 space-y-2">
                {/* Linha 1: Botões Principais lado a lado */}
                <div className="grid grid-cols-2 gap-2">
                  {/* Botão Novo Disparo - só mostra se ativo */}
                  {template.isActive ? (
                    <button
                      onClick={() => handleExecuteTemplate(template)}
                      className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success/90 font-medium text-sm flex items-center justify-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Novo Disparo
                    </button>
                  ) : (
                    <div></div>
                  )}

                  {/* Botão Ver Disparos */}
                  <button
                    onClick={() => navigate(`/waba/${wabaAccountId}/templates/${template.id}/executions`)}
                    className="px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-2 font-medium text-sm flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Ver Disparos
                  </button>
                </div>

                {/* Linha 2: Botões de Gerenciamento */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggleActive(template)}
                    className={`flex-1 px-3 py-2 rounded-lg font-medium text-xs flex items-center justify-center gap-1 ${
                      template.isActive
                        ? 'bg-warning/10 text-warning hover:bg-warning/20'
                        : 'bg-success/10 text-success hover:bg-success/20'
                    }`}
                    title={template.isActive ? 'Desativar modelo' : 'Ativar modelo'}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {template.isActive ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      )}
                    </svg>
                    {template.isActive ? 'Desativar' : 'Ativar'}
                  </button>

                  <button
                    onClick={() => handleDeleteTemplate(template)}
                    className="flex-1 px-3 py-2 bg-danger/10 text-danger rounded-lg hover:bg-danger/20 font-medium text-xs flex items-center justify-center gap-1"
                    title="Excluir modelo"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Deletar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-bg-3 rounded-lg hover:bg-bg-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-lg font-medium ${
                  page === currentPage
                    ? 'bg-accent-1 text-white'
                    : 'bg-bg-2 text-text-1 hover:bg-bg-3 border border-bg-3'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-bg-3 rounded-lg hover:bg-bg-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Próxima
          </button>
        </div>
      )}

      {/* Create Template Modal */}
      <CreateTemplateModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          setCreateModalOpen(false);
          fetchTemplates();
        }}
        initialWabaAccountId={wabaAccountId}
      />

      {/* Execute Template Modal */}
      {selectedTemplate && (
        <ExecuteTemplateModal
          isOpen={executeModalOpen}
          onClose={() => {
            setExecuteModalOpen(false);
            setSelectedTemplate(null);
          }}
          template={selectedTemplate}
          onSuccess={handleExecuteSuccess}
          qualityRating={qualityRating}
        />
      )}

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
